import { useEffect, useState } from 'react';
import { logger } from '@/app/logger';
import { getPublicContent, peekPublicContent } from '../lib/publicContentCache';
import { isArticleDetailPublicPath } from '../lib/isArticleDetailPublicPath';

const log = logger.create('useCachedPublicGet');

interface CachedPublicGetState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useCachedPublicGet<T>(path: string | undefined): CachedPublicGetState<T> {
  const [state, setState] = useState<CachedPublicGetState<T>>(() => {
    if (!path) {
      return { data: null, loading: false, error: null };
    }
    const cached = peekPublicContent<T>(path);
    return {
      data: cached ?? null,
      loading: cached === undefined,
      error: null,
    };
  });

  useEffect(() => {
    if (!path) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    const cached = peekPublicContent<T>(path);
    setState({
      data: cached ?? null,
      loading: cached === undefined,
      error: null,
    });

    const shouldSkipFetch = cached !== undefined && !isArticleDetailPublicPath(path);
    if (shouldSkipFetch) {
      return;
    }

    let cancelled = false;
    if (cached === undefined) {
      setState((current) => ({ ...current, loading: true, error: null }));
    }

    getPublicContent<T>(path)
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Request failed';
        log.error('fetch', 'Cached public GET failed', { path, message });
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, [path]);

  return state;
}
